import React from "react";
import { Link } from "react-router-dom";
import { Button, CircularProgress, MenuItem, Select, Tooltip } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";

import i18n from "i18";
import _request from "actions/_request";
import AvatarIcon from "../AvatarIcon";
import IconsAccounts from "../IconsAccounts";
import ImageError from "../ImageError";
import { ReactComponent as NoData } from "../../images/No upcoming posts.svg";
import {
  DEFAULT_PERIOD,
  MIN_AUDIENCE,
  PERIODS,
  availableMetrics,
  describeError,
  metricChange,
  formatCount,
  formatDay,
  formatPercent,
  formatSigned,
  getAnalyticsAccounts,
  hasSeries,
  isBelowMinAudience,
  isEmptyPeriod,
  networkName,
  seriesToChartData,
  supportsAnalytics,
  thumbnailSrc,
  topPostLink,
  unavailableReasonKey,
} from "../../utils/accountAnalytics";
import "./style.css";

ChartJS.register(CategoryScale, Filler, LinearScale, LineElement, PointElement, ChartTooltip);

const MAX_BEST_POSTS = 5;

const POST_METRICS = [
  { key: "views", Icon: VisibilityOutlinedIcon },
  { key: "likes", Icon: FavoriteBorderIcon },
  { key: "comments", Icon: ChatBubbleOutlineIcon },
  { key: "shares", Icon: ShareOutlinedIcon },
];

/**
 * Colours for the charts. chart.js draws on a canvas, so it cannot read the CSS
 * variables the rest of the page uses; the theme is read from the body class that
 * ThemeContext sets.
 *
 * @returns {Object} Palette for the current theme.
 */
function getPalette() {
  const light = typeof document !== "undefined" && document.body.classList.contains("light");
  return light
    ? { line: "#0a8fa3", line2: "#6b63e0", text: "#212832", grid: "rgba(0,0,0,0.08)" }
    : { line: "#00E5FF", line2: "#B6B4FF", text: "rgba(255,255,255,0.75)", grid: "rgba(255,255,255,0.08)" };
}

/*
 * Analytics of one account for a period: totals with their change, a followers
 * line where the network keeps history, and the best posts.
 *
 * Used twice (OP#3456): on the dashboard with an account picker, and in the
 * dialog of an account card for that one account (`accountId`). Every failure is
 * rendered here from `data.code`; nothing raises a global alert and nothing is
 * faked when the server has no answer.
 *
 * Props: `accounts`, `accountId` (fixed account, no picker), `onReconnect(account)`
 * (without it the button links to the accounts page), `variant` ("panel" | "plain").
 */
export default class AccountAnalytics extends React.Component {
  static defaultProps = { accounts: [], variant: "plain" };

  constructor(props) {
    super(props);
    this.state = {
      accountId: props.accountId ?? null,
      period: DEFAULT_PERIOD,
      status: "idle",
      data: null,
      error: null,
    };
    this._mounted = false;
    this._requestId = 0;
    this._key = null;
  }

  componentDidMount() {
    this._mounted = true;
    this._onLanguage = () => this.forceUpdate();
    if (i18n?.on) i18n.on("languageChanged", this._onLanguage);

    // Charts are drawn on a canvas, so a theme switch has to re-run render.
    if (typeof MutationObserver !== "undefined") {
      this._themeObserver = new MutationObserver(() => this.forceUpdate());
      this._themeObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    }

    this.syncLoad();
  }

  componentDidUpdate() {
    this.syncLoad();
  }

  componentWillUnmount() {
    this._mounted = false;
    if (i18n?.off && this._onLanguage) i18n.off("languageChanged", this._onLanguage);
    if (this._themeObserver) this._themeObserver.disconnect();
  }

  currentAccount() {
    const { accounts, accountId } = this.props;
    const list = Array.isArray(accounts) ? accounts : [];
    if (accountId !== undefined && accountId !== null) return list.find((a) => a.id === accountId) || null;

    const pickable = getAnalyticsAccounts(list);
    return pickable.find((a) => a.id === this.state.accountId) || pickable[0] || null;
  }

  // Loads again whenever the account or the period behind the block changed.
  syncLoad() {
    const account = this.currentAccount();
    const key = account ? `${account.id}|${this.state.period}` : "none";
    if (key !== this._key) this.load();
  }

  load = async () => {
    const account = this.currentAccount();
    const { period } = this.state;
    this._key = account ? `${account.id}|${period}` : "none";
    const requestId = ++this._requestId;

    if (!account) {
      this.setState({ status: "idle", data: null, error: null });
      return;
    }

    // The client knows which networks have analytics; no point asking for the rest.
    if (!supportsAnalytics(account.social_type)) {
      this.setState({
        status: "error",
        data: null,
        error: describeError({ data: { code: "ANALYTICS_NOT_SUPPORTED" } }),
      });
      return;
    }

    this.setState({ status: "loading", error: null });

    let response;
    try {
      response = await _request.accounts.analytics(account.id, period);
    } catch (error) {
      response = null;
    }

    // A slower answer to an earlier choice must not overwrite the current one.
    if (!this._mounted || requestId !== this._requestId) return;

    if (response?.isOk && response.data) {
      this.setState({ status: "ok", data: response.data, error: null });
    } else {
      this.setState({ status: "error", data: null, error: describeError(response) });
    }
  };

  locale() {
    return i18n?.language || "en";
  }

  t(key, options) {
    return i18n.t(key, options);
  }

  // ── Header ────────────────────────────────────────────────────────────────

  renderAccountPicker(pickable, account) {
    return (
      <Select
        size="small"
        fullWidth
        value={account.id}
        onChange={(e) => this.setState({ accountId: e.target.value })}
        inputProps={{ "aria-label": this.t("analytics.account") }}
        MenuProps={{ PaperProps: { sx: { backgroundColor: "var(--background-menu)", color: "var(--text-color)" } } }}
        className="aa-select"
        sx={{
          color: "var(--text-color)",
          ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-color)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent-secondary)" },
          ".MuiSvgIcon-root": { color: "var(--text-color)" },
        }}>
        {pickable.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            <span className="aa-account-option">
              <IconsAccounts key={a.social_type} socialType={a.social_type} width="18px" height="18px" />
              <span className="aa-account-name">{a.username || a.account_name}</span>
              {a.state === "needs_reconnect" && <span className="aa-badge">{this.t("accountsPage.needsReconnect")}</span>}
            </span>
          </MenuItem>
        ))}
      </Select>
    );
  }

  renderAccountTitle(account) {
    return (
      <div className="aa-account-title">
        <AvatarIcon avatar={account.avatar} width="36px" height="36px" />
        <div className="aa-account-text">
          <span className="aa-account-name">{account.username || account.account_name}</span>
          <span className="aa-account-network">
            <IconsAccounts key={account.social_type} socialType={account.social_type} width="14px" height="14px" />
            {networkName(account.social_type)}
          </span>
        </div>
      </div>
    );
  }

  renderPeriods() {
    const { period } = this.state;
    return (
      <div className="aa-tabs" role="tablist" aria-label={this.t("analytics.periodLabel")}>
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={period === p}
            className={`aa-tab${period === p ? " active" : ""}`}
            onClick={() => this.setState({ period: p })}>
            {this.t(`analytics.period.${p}`)}
          </button>
        ))}
      </div>
    );
  }

  // ── States ────────────────────────────────────────────────────────────────

  renderNoAccounts() {
    return (
      <div className="aa-message">
        <p>{this.t("analytics.noAccounts")}</p>
        <Link to="/app/accounts" className="aa-link-button">
          {this.t("analytics.connectAccount")}
        </Link>
      </div>
    );
  }

  renderLoading() {
    return (
      <div className="aa-message" role="status">
        <CircularProgress size={22} thickness={5} sx={{ color: "var(--accent-secondary)" }} />
        <p>{this.t("analytics.loading")}</p>
      </div>
    );
  }

  renderReconnect(account) {
    const { onReconnect } = this.props;
    if (onReconnect) {
      return (
        <button type="button" className="aa-link-button" onClick={() => onReconnect(account)}>
          {this.t("analytics.reconnect")}
        </button>
      );
    }
    return (
      <Link to="/app/accounts" className="aa-link-button">
        {this.t("analytics.reconnect")}
      </Link>
    );
  }

  renderError(account, error) {
    const text = this.t(error.key, { network: networkName(account.social_type), count: MIN_AUDIENCE });
    return (
      <div className="aa-message" role="status">
        <p>{text}</p>
        {error.needsReconnect && this.renderReconnect(account)}
        {error.retryable && (
          <Button size="small" onClick={this.load} sx={{ textTransform: "none", color: "var(--accent-secondary)" }}>
            {this.t("analytics.retry")}
          </Button>
        )}
      </div>
    );
  }

  // ── Content ───────────────────────────────────────────────────────────────

  sparklineOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
      interaction: { intersect: false },
    };
  }

  followersChartOptions(palette) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      locale: this.locale(),
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { display: false } },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: palette.text, maxRotation: 0, autoSkip: true, maxTicksLimit: 6, font: { size: 11 } },
        },
        y: {
          grid: { color: palette.grid },
          border: { display: false },
          ticks: { color: palette.text, maxTicksLimit: 5, font: { size: 11 } },
        },
      },
    };
  }

  // Change against the window right before the period; "—" where the network cannot say (TikTok followers, history too short).
  renderTrend(key, data) {
    const change = metricChange(data, key);
    if (!change) {
      return (
        <Tooltip title={this.t("analytics.noChange")} arrow>
          <div className="aa-stat-trend flat" tabIndex={0}>
            —
          </div>
        </Tooltip>
      );
    }

    const locale = this.locale();
    const percent = change.pct === null || change.abs === 0 ? "" : ` (${formatPercent(change.pct, locale)})`;
    const text = `${this.t("analytics.change", { delta: formatSigned(change.abs, locale), days: data.period?.days })}${percent}`;
    const tone = change.abs > 0 ? "up" : change.abs < 0 ? "down" : "flat";
    return <div className={`aa-stat-trend ${tone}`}>{text}</div>;
  }

  renderStat(key, metric, data, account, palette) {
    const locale = this.locale();
    const series = data.series?.[key];
    const showSpark = metric.available && key !== "followers" && hasSeries(series);
    const sparkData = showSpark
      ? seriesToChartData(series, { color: key === "views" ? palette.line : palette.line2, locale, fill: true })
      : null;

    return (
      <div key={key} className={`aa-stat${metric.available ? "" : " missing"}`}>
        <div className="aa-stat-label">{this.t(`analytics.metrics.${key}`)}</div>
        <div className="aa-stat-value">
          {metric.available ? (
            formatCount(metric.value, locale)
          ) : (
            <Tooltip title={this.t(unavailableReasonKey(metric.reason), { network: networkName(account.social_type), count: MIN_AUDIENCE })} arrow>
              <span className="aa-dash" tabIndex={0}>
                —
              </span>
            </Tooltip>
          )}
        </div>
        {sparkData && (
          <div className="aa-spark">
            <Line data={sparkData} options={this.sparklineOptions()} />
          </div>
        )}
        {metric.available && this.renderTrend(key, data)}
      </div>
    );
  }

  renderFollowersChart(data, followersChart, account, palette) {
    if (!followersChart.available) {
      const key = followersChart.reason === "NOT_PROVIDED" ? "analytics.noFollowersChart" : unavailableReasonKey(followersChart.reason);
      return <p className="aa-note">{this.t(key, { network: networkName(account.social_type), count: MIN_AUDIENCE })}</p>;
    }

    const chartData = seriesToChartData(data.series.followers, {
      label: this.t("analytics.metrics.followers"),
      color: palette.line,
      locale: this.locale(),
      fill: true,
    });

    return (
      <div className="aa-section">
        <h4 className="aa-section-title">{this.t("analytics.followersChart")}</h4>
        <div className="aa-chart">
          <Line data={chartData} options={this.followersChartOptions(palette)} />
        </div>
      </div>
    );
  }

  renderBestPosts(data, account) {
    const posts = (Array.isArray(data.top_posts) ? data.top_posts : []).slice(0, MAX_BEST_POSTS);
    const reason = data.unavailable?.top_posts;
    if (!posts.length && !reason) return null;

    const locale = this.locale();
    const network = networkName(account.social_type);
    // No numbers for the posts is not the same as no permission: the first is Meta's empty answer, the second can be fixed by reconnecting.
    const reasonKey = reason === "NOT_PROVIDED" ? "analytics.postNumbersMissing" : unavailableReasonKey(reason);

    return (
      <div className="aa-section">
        {posts.length > 0 && <h4 className="aa-section-title">{this.t("analytics.bestPosts")}</h4>}
        <ul className="aa-posts">
          {posts.map((post, i) => {
            const thumb = thumbnailSrc(post.thumbnail_url);
            const date = post.published_at ? formatDay(post.published_at, locale) : "";
            const link = topPostLink(post, account.social_type);
            const metrics = POST_METRICS.filter(({ key }) => typeof post[key] === "number");

            const content = (
              <>
                {/* The picture may be a provider's and expire; ImageError falls back to a placeholder. */}
                {thumb && (
                  <ImageError key={thumb} src={thumb} referrerPolicy="no-referrer" className="aa-post-thumb" width="44px" height="44px" />
                )}
                <div className="aa-post-main">
                  <div className="aa-post-title">{post.text || this.t("analytics.postFallback", { date })}</div>
                  {post.text && date && <div className="aa-post-date">{date}</div>}
                  {metrics.length > 0 && (
                    <div className="aa-post-metrics">
                      {metrics.map(({ key, Icon }) => {
                        const label = this.t(`analytics.post.${key}`);
                        const value = formatCount(post[key], locale);
                        return (
                          <Tooltip key={key} title={label} arrow>
                            <span className="aa-post-metric" aria-label={`${label}: ${value}`}>
                              <Icon sx={{ fontSize: 14 }} />
                              {value}
                            </span>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </div>
                {link && <OpenInNewIcon className="aa-post-open" sx={{ fontSize: 14 }} />}
              </>
            );

            return (
              <li key={post.external_id ?? post.task_id ?? i}>
                {link ? (
                  <a className="aa-post aa-post-link" href={link} target="_blank" rel="noopener noreferrer" title={this.t("analytics.openPost")}>
                    {content}
                  </a>
                ) : (
                  <div className="aa-post">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
        {reason && <p className="aa-note">{this.t(reasonKey, { network, count: MIN_AUDIENCE })}</p>}
      </div>
    );
  }

  renderContent(account, data) {
    const palette = getPalette();
    const { metrics, followersChart } = availableMetrics(data);
    const belowMin = isBelowMinAudience(data);
    const empty = !belowMin && isEmptyPeriod(data);
    const locale = this.locale();
    const fetched = data.fetched_at ? new Date(data.fetched_at) : null;

    return (
      <>
        <div className="aa-stats">{Object.keys(metrics).map((key) => this.renderStat(key, metrics[key], data, account, palette))}</div>

        {account.social_type === "tiktok" && (metrics.views.available || metrics.engagements.available) && (
          <p className="aa-note">{this.t("analytics.tiktokTotalsNote")}</p>
        )}

        {!belowMin && this.renderFollowersChart(data, followersChart, account, palette)}

        {belowMin && <p className="aa-note">{this.t("analytics.errors.belowMinAudience", { count: MIN_AUDIENCE })}</p>}

        {empty && (
          <div className="aa-empty">
            <NoData style={{ width: "50%", maxWidth: 160 }} />
            <p className="aa-empty-title">{this.t("analytics.emptyTitle")}</p>
            <p className="aa-note">{this.t("analytics.emptyHint")}</p>
          </div>
        )}

        {!belowMin && this.renderBestPosts(data, account)}

        {fetched && !Number.isNaN(fetched.getTime()) && (
          <p className="aa-updated">
            {this.t("analytics.updatedAt", {
              time: new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(fetched),
            })}
          </p>
        )}
      </>
    );
  }

  renderBody(account) {
    const { status, data, error } = this.state;
    if (status === "loading" || status === "idle") return this.renderLoading();
    if (status === "error") return this.renderError(account, error);
    return this.renderContent(account, data);
  }

  render() {
    const { accounts, accountId, variant } = this.props;
    const fixed = accountId !== undefined && accountId !== null;
    const account = this.currentAccount();
    const pickable = getAnalyticsAccounts(accounts);

    return (
      <section className={`aa-root aa-${variant}`} aria-label={this.t("analytics.title")}>
        {!fixed && <h3 className="aa-title">{this.t("analytics.title")}</h3>}
        {!account ? (
          this.renderNoAccounts()
        ) : (
          <>
            {fixed ? this.renderAccountTitle(account) : this.renderAccountPicker(pickable, account)}
            {supportsAnalytics(account.social_type) && this.renderPeriods()}
            {this.renderBody(account)}
          </>
        )}
      </section>
    );
  }
}
